import {
  createAction,
} from '@cobuildlab/react-simple-state';
import {
  NewMetatraderAccountDto,
} from 'metaapi.cloud-sdk';
import { MetatraderAccount } from 'metaapi.cloud-sdk';

import {
  createMetaApiAccountErrorEvent,
  createMetaApiAccountEvent,
  updateMetaApiAccountErrorEvent,
  updateMetaApiAccountEvent,
  updateMetaApiBrokerErrorEvent,
  updateMetaApiBrokerEvent,
} from './meta-api-cloud-events';
import axios from 'axios';
import { fetchUserEvent, OnTokenEvent } from '../auth/auth-events';
import { BACKEND_URL, META_API_CLOUD_PROVISIONING_API, META_API_TOKEN } from '../../shared/contants';
import { MetaApiCloudAccount as MetaApiCloudAccountGraphql } from '../../shared/types/generated';
import {
  CreateMetaApiCloudAccountResponse,
  MetaStatTrade,
  MetaStatTradeResponse,
  UpdateMetaApiBrokerData,
  MetaApiCloudAccount, UpdateMetaApiCloudAccountResponse,
} from './meta-api-cloud-types';
import MetaApi, {
  StreamingMetaApiConnectionInstance,
} from 'metaapi.cloud-sdk';
import { MetaApiListener } from './MetaApiListener';
import moment from 'moment';

export const connectToMetaApi = async (
  metaApiCloudAccount: MetaApiCloudAccountGraphql | null | undefined,
): Promise<StreamingMetaApiConnectionInstance | null> => {


  if (!metaApiCloudAccount) return null;

  // Get instance of MetaApi with your MetaApi token
  const metaApi = new MetaApi(META_API_TOKEN, {
    region: metaApiCloudAccount.region as string,
    domain: 'agiliumtrade.agiliumtrade.ai',
  });



  // Get MetaTrader account
  const account = await metaApi.metatraderAccountApi.getAccount(
    metaApiCloudAccount.account_id as string,
  );
  const initialState = account.state;
  const deployedStates = ['DEPLOYING', 'DEPLOYED'];

  if (!deployedStates.includes(initialState)) {
    // wait until account is deployed and connected to broker
    await account.deploy();
  }

  // Get connection instance
  await account.waitConnected();

  const connection = account.getStreamingConnection();
  const listener = new MetaApiListener();
  connection.addSynchronizationListener(listener);
  // Wait until connection is established
  await connection.connect();
  await connection.waitSynchronized();
  return connection;
};

export const createMetaApiAccount = createAction(
  createMetaApiAccountEvent,
  createMetaApiAccountErrorEvent,
  async (data: NewMetatraderAccountDto) => {


    const createMetaApiAccountCloudResponse = await axios.post<CreateMetaApiCloudAccountResponse>(
      `${META_API_CLOUD_PROVISIONING_API}/users/current/accounts`,
      { ...data, name: data.login },
      {
        headers: {
          'auth-token': META_API_TOKEN,
        },
      },
    );

    const metaApiCloudAccountResponse = await axios.get<MetaApiCloudAccount>(
      `${META_API_CLOUD_PROVISIONING_API}/users/current/accounts/${createMetaApiAccountCloudResponse.data.id}`,
      {
        headers: {
          'auth-token': META_API_TOKEN,
        },
      },
    );

    const metaApiCloudAccount = metaApiCloudAccountResponse.data;

    return {
      account: metaApiCloudAccount,
    };
  },
);

export const updateMetaApiAccount = createAction(
  updateMetaApiAccountEvent,
  updateMetaApiAccountErrorEvent,
  async (data: NewMetatraderAccountDto, accountId: string) => {

    await axios.put<UpdateMetaApiCloudAccountResponse>(
      `${META_API_CLOUD_PROVISIONING_API}/users/current/accounts/${accountId}`,
      {
        copyFactoryResourceSlots: data.copyFactoryResourceSlots,
        login: data.login,
        magic: data.magic,
        manualTrades: data.manualTrades,
        name: data.name,
        password: data.password,
        quoteStreamingIntervalInSeconds: data.quoteStreamingIntervalInSeconds,
        resourceSlots: data.resourceSlots,
        server: data.server,
        slippage: data.slippage,
      },
      {
        headers: {
          'auth-token': META_API_TOKEN,
        },
      },
    );

    const metaApiCloudAccountResponse = await axios.get<MetaApiCloudAccount>(
      `${META_API_CLOUD_PROVISIONING_API}/users/current/accounts/${accountId}`,
      {
        headers: {
          'auth-token': META_API_TOKEN,
        },
      },
    );

    return {
      account: metaApiCloudAccountResponse.data,
    };
  },
);

export const updateMetaApiBroker = createAction(
  updateMetaApiBrokerEvent,
  updateMetaApiBrokerErrorEvent,
  async (data: UpdateMetaApiBrokerData) => {
    const token = OnTokenEvent.get()?.token;

    const user = fetchUserEvent.get()?.user;

    const account = await axios.post<MetatraderAccount>(
      `${BACKEND_URL}/meta-api-cloud/update-broker`,
      {
        userId: user?.id,
        account: data,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    return {
      account: account.data,
    };
  },
);


export const metaStatsOpenTrades = async (accountId: string, region: string): Promise<MetaStatTrade[]> => {
  let trades: MetaStatTrade[] = [];

  try {
    const startDate = moment()
      .utc(true)
      .startOf('year')
      .format('YYYY-MM-DD HH:mm:ss.SSS');
    const endDate = moment()
      .utc(true)
      .endOf('year')
      .format('YYYY-MM-DD HH:mm:ss.SSS');
    const response = await axios.get<MetaStatTradeResponse>(
      `https://metastats-api-v1.${region}.agiliumtrade.ai/users/current/accounts/${accountId}/historical-trades/${startDate}/${endDate}?updateHistory=true&limit=1000&offset=0`,
      {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'auth-token': META_API_TOKEN,
        },
      },
    );
    trades = response.data.trades || [];
  } catch (err) {
    console.error(err);
  }

  return trades;
};


